import { useState, useCallback, useEffect, useRef } from "react";
import { FileUp, Car, CheckCircle, X, FileCode } from "lucide-react";
import { parseVehicleXML } from "@/utils/xmlParser";

const VehicleXMLDropzone = ({ onVehicleData, isVisible, onClose }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState("");
  const [parsedData, setParsedData] = useState(null);
  const dropzoneRef = useRef(null);

  // Reset state when visibility changes
  useEffect(() => {
    if (!isVisible) {
      setIsProcessing(false);
      setIsSuccess(false);
      setError(null);
      setFileName("");
      setParsedData(null);
    }
  }, [isVisible]);

  const processFile = useCallback(async (file) => {
    console.log("Processing file:", file.name);
    setIsProcessing(true);
    setError(null);
    setFileName(file.name);

    try {
      const text = await file.text();
      console.log("File content length:", text.length);
      const vehicleData = parseVehicleXML(text);
      console.log("Parsed vehicle data:", vehicleData);
      
      if (!vehicleData.brand || vehicleData.brand === "Unbekannt") {
        throw new Error("Keine gültigen Fahrzeugdaten in der Datei gefunden");
      }

      setParsedData(vehicleData);
      setIsSuccess(true);
      setTimeout(() => {
        onVehicleData(vehicleData);
      }, 1500);
    } catch (err) {
      console.error("Error processing file:", err);
      setError(err.message || "Fehler beim Verarbeiten der Datei");
      setIsProcessing(false);
    }
  }, [onVehicleData]);

  // Handle drop on the dropzone element
  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log("Drop event received in Dropzone");

    const files = e.dataTransfer?.files;
    console.log("Files:", files?.length);
    
    if (files && files.length > 0) {
      const file = files[0];
      const name = file.name.toLowerCase();
      console.log("File name:", name);
      
      if (name.endsWith('.xml') || name.endsWith('.vxs')) {
        processFile(file);
      } else {
        setError("Bitte nur XML oder VXS Dateien verwenden");
      }
    }
  }, [processFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'copy';
    }
  }, []);

  const handleClose = useCallback(() => {
    if (!isProcessing && !isSuccess) {
      onClose();
    }
  }, [isProcessing, isSuccess, onClose]);

  // Register a native drop listener to ensure we catch the event
  useEffect(() => {
    if (!isVisible || !dropzoneRef.current) return;

    const dropzone = dropzoneRef.current;
    
    const onNativeDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      console.log("Native drop event on dropzone");
      
      const files = e.dataTransfer?.files;
      if (files && files.length > 0) {
        const file = files[0];
        const name = file.name.toLowerCase();
        if (name.endsWith('.xml') || name.endsWith('.vxs')) {
          processFile(file);
        } else {
          setError("Bitte nur XML oder VXS Dateien verwenden");
        }
      }
    };

    const onNativeDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }
    };

    dropzone.addEventListener('drop', onNativeDrop, true);
    dropzone.addEventListener('dragover', onNativeDragOver, true);

    return () => {
      dropzone.removeEventListener('drop', onNativeDrop, true);
      dropzone.removeEventListener('dragover', onNativeDragOver, true);
    };
  }, [isVisible, processFile]);

  if (!isVisible) return null;

  return (
    <div 
      ref={dropzoneRef}
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      style={{
        background: 'rgba(0, 0, 0, 0.92)',
        backdropFilter: 'blur(8px)',
      }}
      data-testid="xml-dropzone"
    >
      {/* Click outside to close */}
      <div 
        className="absolute inset-0" 
        onClick={handleClose}
        style={{ pointerEvents: 'auto' }}
      />

      {/* Dropzone Container - pointer-events-none to prevent bubbling issues */}
      <div 
        className="relative w-[600px] h-[450px] pointer-events-none"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Border */}
        <div 
          className={`
            absolute inset-0 border-4 transition-colors duration-300
            ${isSuccess 
              ? "border-green-500 bg-green-500/10" 
              : error 
                ? "border-red-500 bg-red-500/10"
                : "border-[#EFDF00] bg-[#EFDF00]/10"
            }
          `}
        />

        {/* Corner accents */}
        <div className="absolute -top-1 -left-1 w-16 h-16 border-t-4 border-l-4 border-[#EFDF00]" />
        <div className="absolute -top-1 -right-1 w-16 h-16 border-t-4 border-r-4 border-[#EFDF00]" />
        <div className="absolute -bottom-1 -left-1 w-16 h-16 border-b-4 border-l-4 border-[#EFDF00]" />
        <div className="absolute -bottom-1 -right-1 w-16 h-16 border-b-4 border-r-4 border-[#EFDF00]" />

        {/* Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12">
          {isProcessing && !isSuccess ? (
            // Processing State
            <div className="text-center">
              <div className="w-24 h-24 mx-auto mb-8 relative">
                <div className="absolute inset-0 border-4 border-[#EFDF00]/30" />
                <div className="absolute inset-0 border-4 border-[#EFDF00] border-t-transparent animate-spin" />
                <FileCode className="absolute inset-0 m-auto w-10 h-10 text-[#EFDF00]" />
              </div>
              <p className="font-nouvel text-2xl text-white mb-3">Verarbeite Daten...</p>
              <p className="font-read text-sm text-white/60">{fileName}</p>
            </div>
          ) : isSuccess ? (
            // Success State
            <div className="text-center">
              <div className="w-24 h-24 bg-green-500 mx-auto mb-6 flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-white" />
              </div>
              <p className="font-nouvel text-2xl text-green-400 mb-4">Fahrzeug erkannt!</p>
              
              {parsedData && (
                <div className="bg-white/10 p-4 text-left max-w-md mx-auto mb-4">
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="text-white/60">Marke:</div>
                    <div className="text-white font-bold">{parsedData.brand}</div>
                    
                    <div className="text-white/60">Modell:</div>
                    <div className="text-white font-bold">{parsedData.model}</div>
                    
                    {parsedData.year && (
                      <>
                        <div className="text-white/60">Baujahr:</div>
                        <div className="text-white">{parsedData.year}</div>
                      </>
                    )}
                    
                    {parsedData.mileage > 0 && (
                      <>
                        <div className="text-white/60">Kilometerstand:</div>
                        <div className="text-white">{parsedData.mileage.toLocaleString()} km</div>
                      </>
                    )}
                    
                    {parsedData.color && (
                      <>
                        <div className="text-white/60">Farbe:</div>
                        <div className="text-white">{parsedData.color}</div>
                      </>
                    )}
                    
                    {parsedData.vin && (
                      <>
                        <div className="text-white/60">FIN:</div>
                        <div className="text-white font-mono text-xs">{parsedData.vin}</div>
                      </>
                    )}
                  </div>
                  
                  {parsedData.features?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/20">
                      <p className="text-xs text-green-400">{parsedData.features.length} Ausstattungsmerkmale geladen</p>
                    </div>
                  )}
                </div>
              )}
              
              <p className="font-read text-sm text-white/60">Formular wird ausgefüllt...</p>
            </div>
          ) : error ? (
            // Error State
            <div className="text-center">
              <div className="w-24 h-24 bg-red-500/20 border-4 border-red-500 mx-auto mb-8 flex items-center justify-center">
                <X className="w-12 h-12 text-red-500" />
              </div>
              <p className="font-nouvel text-2xl text-red-400 mb-3">Fehler</p>
              <p className="font-read text-sm text-white/60 mb-6">{error}</p>
              <button
                onClick={() => setError(null)}
                className="px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-read transition-colors"
              >
                Erneut versuchen
              </button>
            </div>
          ) : (
            // Drag State - immer bereit für Drop
            <>
              <div className="w-28 h-28 mb-8 flex items-center justify-center bg-[#EFDF00]">
                <Car className="w-14 h-14 text-black" />
              </div>
              
              <h2 className="font-nouvel text-3xl mb-4 text-[#EFDF00]">
                Jetzt loslassen!
              </h2>
              
              <p className="font-read text-lg text-white/60 text-center mb-2">
                Legen Sie die <span className="text-[#EFDF00] font-bold">XML</span> oder <span className="text-[#EFDF00] font-bold">VXS</span> Datei hier ab
              </p>
              
              <p className="font-read text-sm text-white/40">
                DAT Fahrzeugbewertungen werden automatisch erkannt
              </p>
            </>
          )}
        </div>
      </div>

      {/* Bottom hint */}
      <div className="absolute bottom-10 text-center">
        <p className="font-read text-xs text-white/30">
          ESC oder außerhalb klicken zum Schließen
        </p>
      </div>
    </div>
  );
};

export default VehicleXMLDropzone;
